import { Dialog, DialogActions, DialogContent, FormControlLabel, Grid, Radio, RadioGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import ModalActionBar from 'components/modal-action-bar';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateSettingModal } from './hooks';
import { useStyles } from './styles';
import { ChargeSetting } from 'graphql/charge-settings/queries';
import { GQLCreateChargeSettingPayload, GQLUpdateChargeSettingPayload } from 'graphql/charge-settings/mutations';
import NumberField from 'components/number-field';
import { convertPercentStringToNumber, formatValue, isNumberAllowed } from 'views/charge-settings/utils';

type Props = {
  onClose: () => void,
  onCreate: (data: GQLCreateChargeSettingPayload) => void,
  onUpdate: (data: GQLUpdateChargeSettingPayload) => void,
  isCreateLoading: boolean,
  isUpdateLoading: boolean,
  prevSettingData?: ChargeSetting
};

export const CreateSettingModal: FC<Props> = ({
  onClose,
  onCreate,
  onUpdate,
  isCreateLoading,
  isUpdateLoading,
  prevSettingData
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const {
    settingForm,
    onConfirm,
    allowedListings,
    allowedLetters,
    isDefaultForAllSetting,
    payeesFieldArray,
    payersFieldArray,
  } = useCreateSettingModal({
    onCreate,
    onUpdate,
    prevSettingData
  });

  const { control, setValue, watch, getValues } = settingForm;

  const isSubmitting = isCreateLoading || isUpdateLoading;
  // Fields locked for the default-for-all setting stay locked; everything locks while a request is in flight.
  const isBusinessLocked = isDefaultForAllSetting || isSubmitting;

  const [
    buyer_at,
    buyer_before,
    seller_at,
    seller_before,
    by_others,
    kind
  ] = watch(["buyer_at",
    "buyer_before",
    "seller_at",
    "seller_before",
    "by_others", "kind"]);

  const onChangePercent = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: "buyer_at" | "buyer_before" | "seller_at" | "seller_before" | "by_others") => {
    const percents = [
      {
        name: "buyer_at",
        value: buyer_at
      },
      {
        name: "buyer_before",
        value: buyer_before
      },
      {
        name: "seller_at",
        value: seller_at
      },
      {
        name: "seller_before",
        value: seller_before
      },
      {
        name: "by_others",
        value: by_others
      }
    ];

    const number = convertPercentStringToNumber(e);
    const totalPercent = percents.filter(percent => percent.name !== fieldName).reduce((accumulator, percent) => accumulator + (percent.value ?? 0), 0);

    let value = number;
    if (totalPercent + number > 1) {
      value = 0;
    }

    setValue(fieldName, value, {
      shouldDirty: true
    });
  };

  const onPercentChange = (index: number, list: 'payees' | 'payers') => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const number = convertPercentStringToNumber(e);
    const othersTotal = (getValues(list) ?? []).reduce((accumulator, party, partyIndex) => (
      partyIndex === index ? accumulator : accumulator + (party.percent ?? 0)
    ), 0);

    // Compare in basis points to avoid floating point drift (e.g. 0.7 + 0.3).
    const exceedsTotal = Math.round((othersTotal + number) * 10000) > 10000;

    setValue(`${list}.${index}.percent`, exceedsTotal ? 0 : number, { shouldDirty: true });
  };

  const allowedListingsPayers = useMemo(() => {
    return allowedListings.filter(rol => rol.code !== 'Buyer' && rol.code !== 'Seller');
  }, [allowedListings]);

  return (
    <Dialog open fullWidth maxWidth="sm" fullScreen={isCompact}>
      <DialogContent className={classes.content}>
        <Grid container direction="column">
          <Grid item>
            <Typography variant="h5" className={classes.title}>
              {prevSettingData ? t('common:updateChargeSettingTitle') : t('common:createChargeSettingTitle')}
            </Typography>
          </Grid>
          <Grid item container className={classes.container}>
            <Grid item xs={12}>
              <RadioGroup
                name="kind"
                value={kind}
                onChange={(_, value) => {
                  const valueKind = value;

                  if (valueKind === "by-letter-number") {
                    setValue("code", null);
                  }

                  if (valueKind === "code") {
                    setValue("letter", null);
                    setValue("number", null);
                  }

                  setValue("kind", valueKind as "by-letter-number" | "code");
                }}
              >
                <FormControlLabel
                  value="by-letter-number"
                  disabled={isBusinessLocked}
                  control={<Radio />}
                  label={t('common:byLetterAndNumberLabel')}
                />
                <FormControlLabel
                  value="code"
                  disabled={isBusinessLocked}
                  control={<Radio />}
                  label={t('common:byCodeLabel')}
                />
              </RadioGroup>
            </Grid>

            {kind === "by-letter-number" && (
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="letter"
                    render={({ field }) => (
                      <SelectField
                        label={t('common:letterLabel')}
                        value={field.value || ""}
                        handleBlur={field.onBlur}
                        handleChange={field.onChange}
                        data={allowedLetters}
                        dataKey="code"
                        dataValue="code"
                        dataText="description"
                        disabled={isBusinessLocked}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="number"
                    render={({ field }) => (
                      <TextField
                        color="secondary"
                        label={<Typography variant="body2">{t('common:numberLabel')}</Typography>}
                        name="number"
                        handleBlur={field.onBlur}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isBusinessLocked}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}

            {kind === "code" && (
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <TextField
                    color="secondary"
                    label={<Typography variant="body2">{t('common:codeLabel')}</Typography>}
                    name="code"
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isBusinessLocked}
                  />
                )}
              />
            )}
            <Grid item container direction="column" spacing={1}>
              <Grid item container justifyContent="space-between" alignItems="center" className={classes.touchTargets}>
                <Typography variant="subtitle1">{t('common:payeesLabel')}</Typography>
                <ContainedButton
                  text={t('common:addPayeeLabel')}
                  onClick={() => payeesFieldArray.append({ role: '', percent: 0 })}
                  disabled={isBusinessLocked}
                />
              </Grid>
              {payeesFieldArray.fields.map((item, index) => (
                <Grid item container direction="row" spacing={2} alignItems="center" key={item.id}>
                  <Grid item xs={12} sm={5}>
                    <Controller
                      control={control}
                      name={`payees.${index}.role`}
                      render={({ field }) => (
                        <SelectField
                          label={t('common:roleLabel')}
                          value={field.value || ""}
                          handleBlur={field.onBlur}
                          handleChange={field.onChange}
                          data={allowedListings}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          disabled={isBusinessLocked}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={8} sm={5}>
                    <Controller
                      control={control}
                      name={`payees.${index}.percent`}
                      render={({ field }) => (
                        <NumberField
                          color="secondary"
                          value={formatValue(field.value ?? undefined)}
                          isAllowed={isNumberAllowed}
                          onChange={onPercentChange(index, 'payees')}
                          suffix="%"
                          disabled={isBusinessLocked}
                          InputProps={{ inputProps: { 'aria-label': `${t('common:percentageLabel')} ${t('common:payeeLabel')} ${index + 1}` } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2} className={classes.removeCell}>
                    <ContainedButton
                      text={t('common:removeLabel')}
                      onClick={() => payeesFieldArray.remove(index)}
                      disabled={isBusinessLocked}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item container direction="column" spacing={1}>
              <Grid item container justifyContent="space-between" alignItems="center" className={classes.touchTargets}>
                <Typography variant="subtitle1">{t('common:payersLabel')}</Typography>
                <ContainedButton
                  text={t('common:addPayerLabel')}
                  onClick={() => payersFieldArray.append({ role: '', percent: 0 })}
                  disabled={isBusinessLocked}
                />
              </Grid>
              {payersFieldArray.fields.map((item, index) => (
                <Grid item container direction="row" spacing={2} alignItems="center" key={item.id}>
                  <Grid item xs={12} sm={5}>
                    <Controller
                      control={control}
                      name={`payers.${index}.role`}
                      render={({ field }) => (
                        <SelectField
                          label={t('common:roleLabel')}
                          value={field.value || ""}
                          handleBlur={field.onBlur}
                          handleChange={field.onChange}
                          data={allowedListingsPayers}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          disabled={isBusinessLocked}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={8} sm={5}>
                    <Controller
                      control={control}
                      name={`payers.${index}.percent`}
                      render={({ field }) => (
                        <NumberField
                          color="secondary"
                          value={formatValue(field.value ?? undefined)}
                          isAllowed={isNumberAllowed}
                          onChange={onPercentChange(index, 'payers')}
                          suffix="%"
                          disabled={isBusinessLocked}
                          InputProps={{ inputProps: { 'aria-label': `${t('common:percentageLabel')} ${t('common:payersLabel')} ${index + 1}` } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2} className={classes.removeCell}>
                    <ContainedButton
                      text={t('common:removeLabel')}
                      onClick={() => payersFieldArray.remove(index)}
                      disabled={isBusinessLocked}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="buyer_before"
                  render={({ field }) => (
                    <NumberField
                      customRef={field.ref}
                      color="secondary"
                      label={t('common:buyerBeforeLabel')}
                      name="buyer-before"
                      handleBlur={field.onBlur}
                      value={formatValue(field.value ?? undefined)}
                      isAllowed={isNumberAllowed}
                      onChange={(e) => onChangePercent(e, field.name)}
                      suffix="%"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="buyer_at"
                  render={({ field }) => (
                    <NumberField
                      customRef={field.ref}
                      color="secondary"
                      label={t('common:buyerAtLabel')}
                      name="buyer-at"
                      handleBlur={field.onBlur}
                      value={formatValue(field.value ?? undefined)}
                      isAllowed={isNumberAllowed}
                      onChange={(e) => onChangePercent(e, field.name)}
                      suffix="%"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="seller_before"
                  render={({ field }) => (
                    <NumberField
                      customRef={field.ref}
                      color="secondary"
                      label={t('common:sellerBeforeLabel')}
                      name="seller-before"
                      handleBlur={field.onBlur}
                      value={formatValue(field.value ?? undefined)}
                      isAllowed={isNumberAllowed}
                      onChange={(e) => onChangePercent(e, field.name)}
                      suffix="%"
                      disabled={isSubmitting}
                    />
                  )}
                />

              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="seller_at"
                  render={({ field }) => (
                    <NumberField
                      customRef={field.ref}
                      color="secondary"
                      label={t('common:sellerAtLabel')}
                      name="seller-at"
                      handleBlur={field.onBlur}
                      value={formatValue(field.value ?? undefined)}
                      isAllowed={isNumberAllowed}
                      onChange={(e) => onChangePercent(e, field.name)}
                      suffix="%"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Controller
              control={control}
              name="by_others"
              render={({ field }) => (
                <NumberField
                  customRef={field.ref}
                  color="secondary"
                  label={t('common:byOthersLabel')}
                  name="by-others"
                  handleBlur={field.onBlur}
                  value={formatValue(field.value ?? undefined)}
                  isAllowed={isNumberAllowed}
                  onChange={(e) => onChangePercent(e, field.name)}
                  suffix="%"
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              control={control}
              name="amount_override"
              render={({ field }) => (
                <NumberField
                  color="secondary"
                  label={t('common:presetAmountLabel')}
                  name="preset-amount"
                  handleBlur={field.onBlur}
                  value={field.value}
                  onValueChange={({ floatValue }) => field.onChange(floatValue)}
                  prefix="$"
                  thousandSeparator
                  disabled={isSubmitting}
                />
              )}
            />

          </Grid>
        </Grid>
      </DialogContent>
      <ModalActionBar
        onCancel={onClose}
        submitType="button"
        submitText={t('common:confirm')}
        onSubmit={onConfirm}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};
